import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Flex,
  VStack,
  Divider,
  Spinner,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Checkbox,
} from '@chakra-ui/react';
import {
  getallDeviceWithRegisters,
  postFilterReport,
  downloadReport,
} from '../api/filteredreportapi';
import GeneralReportTable from '../components/generalReportTable';
import ExportReportModal from '../components/exportReportModal';

const ReportPage = () => {
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectedRegisters, setSelectedRegisters] = useState([]);
  const [report_type, setreport_type] = useState('Manual');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reportData, setReportData] = useState(null);
  const [isOpenDataModal, setIsOpenDataModal] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (isOpenDataModal) {
      setSelectedDevice(null);
      setSelectedRegisters([]);
    }
  }, [isOpenDataModal]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getallDeviceWithRegisters();
        const extractedDevices = response.flatMap(item => item.device);
        setDevices(extractedDevices);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleSelectRegister = registerId => {
    setSelectedRegisters(prev =>
      prev.includes(registerId)
        ? prev.filter(id => id !== registerId)
        : [...prev, registerId]
    );
  };

  const handleCreateReport = async () => {
    setIsLoading(true);
    const begin = new Date(`${startDate}T${startTime || '00:00'}`);
    const end = new Date(`${endDate}T${endTime || '23:59'}`);
    try {
      const data = await postFilterReport({
        begin,
        end,
        report_type,
        tag_uuids: selectedRegisters,
        pageNumber,
        pageSize,
      });
      if (data.Items) {
        setReportData(data.Items);
        setTotalCount(data.TotalCount);
      } else {
        setReportData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error creating report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderPagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    return (
      <Flex mt={4} justifyContent="center" alignItems="center" gap={4}>
        <Button
          onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
          isDisabled={pageNumber === 1}
        >
          Previous
        </Button>
        <Box>
          Page {pageNumber} of {totalPages}
        </Box>
        <Button
          onClick={() => setPageNumber(prev => Math.min(prev + 1, totalPages))}
          isDisabled={pageNumber >= totalPages}
        >
          Next
        </Button>
      </Flex>
    );
  };

  useEffect(() => {
    setPageNumber(1);
  }, [startDate, startTime, endDate, endTime, report_type, selectedRegisters]);

  useEffect(() => {
    if (reportData !== null) {
      handleCreateReport(); // pageNumber değiştiğinde tekrar çek
    }
  }, [pageNumber, pageSize]);

  const handleClear = () => {
    setSelectedDevices([]);
    setSelectedRegisters([]);
    setStartDate('');
    setStartTime('');
    setEndDate('');
    setEndTime('');
    setReportData(null);
  };
  const handleExport = () => {
    setIsExportModalOpen(true);
  };

  const handleDownloadReport = fileType => {
    const begin = new Date(
      `${startDate}T${startTime || '00:00'}`
    ).toISOString();
    const end = new Date(`${endDate}T${endTime || '23:59'}`).toISOString();

    downloadReport(fileType, {
      begin,
      end,
      report_type,
      tag_uuids: selectedRegisters,
    });
  };

  const handlePrint = () => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        body * {
          visibility: hidden;
        }
        .report-section, .report-section * {
          visibility: visible;
        }
        .report-section {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: auto;
          overflow: visible; 
        }
        table {
          width: 100% !important;
          max-width: 100% !important;
          table-layout: fixed !important;
          border-collapse: collapse;
          word-wrap: break-word;
        }
        thead {
          display: table-header-group; 
        }
        tr, th, td {
          padding: 4px !important;
          font-size: 11px !important;
          line-height: 1.2 !important;
          border: 1px solid black !important;
          word-wrap: break-word !important;
          white-space: normal !important; 
          text-align: left !important; 
          overflow: hidden !important;
        }
        tbody {
          display: table-row-group;
        }
        th {
          max-width: 200px !important; 
          min-width: 50px !important;
          overflow: hidden !important;
        }
        .no-print {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(style);

    window.print();

    document.head.removeChild(style);
  };

  return (
    <Flex direction="column" w="full" h="100vh" overflow="hidden">
      <Box bg="gray.100" p={4} rounded="md" className="no-print">
        <Flex justify="space-between" align="center" wrap="wrap">
          <VStack align="flex-start" spacing={4}>
            <Flex align="center" w="100%">
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" mr={2}>
                  Report Type:
                </FormLabel>
                <Select
                  value={report_type}
                  onChange={e => setreport_type(e.target.value)}
                  width="200px"
                >
                  <option value="Manual">Manual</option>
                  <option value="Hourly">Hourly</option>
                  <option value="Daily">Daily</option>
                </Select>
              </FormControl>
            </Flex>
            <Flex align="center" w="100%">
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="" mr={2} ml="14">
                  Data:
                </FormLabel>
                <Button
                  colorScheme="blue"
                  onClick={() => setIsOpenDataModal(true)}
                  width="200px"
                >
                  Select
                </Button>
              </FormControl>
            </Flex>
            <Modal
              isOpen={isOpenDataModal}
              onClose={() => setIsOpenDataModal(false)}
            >
              <ModalOverlay />
              <ModalContent maxH="70vh" display="flex" flexDirection="column">
                <ModalHeader>Select Devices and Registers</ModalHeader>
                <ModalBody overflowY="auto">
                  <Accordion allowToggle>
                    {devices.map((device, index) => (
                      <AccordionItem key={index}>
                        <AccordionButton
                          onClick={() => {
                            if (report_type === 'Manual') {
                              setSelectedDevice(device.name);
                              setSelectedRegisters([]); // Önceki seçimleri temizle
                            }
                          }}
                          isDisabled={
                            report_type === 'Manual' &&
                            selectedDevice &&
                            selectedDevice !== device.name
                          } // Sadece tek cihaz seçilsin
                        >
                          <Box flex="1" textAlign="left">
                            {device.name}
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                          <VStack align="flex-start" spacing={2}>
                            {device.tag
                              .map(register => (
                                <Checkbox
                                  key={register.id}
                                  value={register.id}
                                  isChecked={selectedRegisters.includes(
                                    register.id
                                  )}
                                  onChange={e =>
                                    handleSelectRegister(e.target.value)
                                  }
                                  pl={6}
                                >
                                  {register.name}
                                </Checkbox>
                              ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </ModalBody>

                <ModalFooter>
                  <Button
                    colorScheme="blue"
                    onClick={() => setIsOpenDataModal(false)}
                  >
                    I'm Done
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </VStack>

          <VStack align="flex-start" spacing={4} mt={4}>
            <Flex align="flex-start" justify="space-between" w="100%">
              <VStack align="flex-start" spacing={4}>
                <FormControl>
                  <FormLabel>Start Date</FormLabel>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>End Date</FormLabel>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                  />
                </FormControl>
              </VStack>
              <VStack align="flex-start" spacing={4} ml={4}>
                <FormControl>
                  <FormLabel>Start Time</FormLabel>
                  <Input
                    type="time"
                    value={startTime}
                    onChange={e => setStartTime(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>End Time</FormLabel>
                  <Input
                    type="time"
                    value={endTime}
                    onChange={e => setEndTime(e.target.value)}
                  />
                </FormControl>
              </VStack>
            </Flex>
          </VStack>
          <VStack align="flex-start" spacing={2}>
            <Button
              bg="gray.700"
              color="white"
              onClick={handleCreateReport}
              w="full"
              className="no-print"
            >
              Apply
            </Button>
            <Button
              bg="gray.700"
              color="white"
              onClick={handleClear}
              w="full"
              className="no-print"
            >
              Clear
            </Button>
            <Button
              bg="gray.700"
              color="white"
              onClick={handleExport}
              w="full"
              className="no-print"
            >
              Export
            </Button>
          </VStack>
        </Flex>
      </Box>
      <Divider my={4} />
      <Box
        flex="1"
        overflowY="auto"
        maxHeight="calc(100vh - 200px)"
        className="report-section"
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <Spinner size="xl" />
          </Box>
        ) : !reportData ? (
          <Box>No data to display</Box>
        ) : (
          <>
            <GeneralReportTable data={reportData} />
            <Box className="no-print">{renderPagination()}</Box>
          </>
        )}
      </Box>

      <Button
        position="fixed"
        bottom="20px"
        right="20px"
        colorScheme="green"
        onClick={handlePrint}
        className="no-print"
      >
        Print
      </Button>
      <ExportReportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onDownload={handleDownloadReport}
      />
    </Flex>
  );
};

export default ReportPage;
